import { forwardRef } from '@chakra-ui/react';
import type { HTMLChakraProps } from '@chakra-ui/react';
import { chakra, useMultiStyleConfig } from '@chakra-ui/system';
import { cx } from '@chakra-ui/utils';

import { Skeleton } from '@frontend/components';

import { PercentageBreakdownBarSegment } from './PercentageBreakdownBarSegment';
import { usePercentageBreakdownContext } from './context';

export type PercentageBreakdownBarProps = HTMLChakraProps<'div'>;

/**
 * PercentageBreakdownBar displays a horizontal bar with color-coded segments
 * representing the data from the PercentageBreakdown context.
 */
export const PercentageBreakdownBar = forwardRef<
  PercentageBreakdownBarProps,
  'div'
>(({ className, ...props }, ref) => {
  const { data, size, variant } = usePercentageBreakdownContext();
  const styles = useMultiStyleConfig('PercentageBreakdown', { size, variant });
  const _className = cx('percentage-breakdown__bar', className);

  const barSegments = data
    .filter((item) => item.value > 0)
    .map((item) => (
      <PercentageBreakdownBarSegment
        key={`${item.label}-${item.value}-${item.color}`}
        bg={item.color}
        flexGrow={item.value}
      />
    ));

  return (
    <chakra.div __css={styles.bar} {...props} className={_className} ref={ref}>
      {barSegments.length > 0 ? (
        barSegments
      ) : (
        <Skeleton flexGrow={1}>
          <PercentageBreakdownBarSegment width="100%" />
        </Skeleton>
      )}
    </chakra.div>
  );
});
PercentageBreakdownBar.displayName = 'PercentageBreakdownBar';
